import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { Switch } from '@/_shared/components/ui/switch';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';

import { SettingsSectionHeader } from '../components/SettingsSectionHeader';
import { useAiControlsMutation } from '../hooks/useSettingsSwitchMutations';
import { AI_CONTROLS } from '../lib/settingsSwitches';

const ADMIN_ONLY = 'Only workspace admins can change these controls.';

/**
 * The four switches that decide which workspace knowledge the agent can use or save, and whether a new
 * memory needs an admin's approval. Admins change them. Members see them disabled, with the reason.
 */
export function AiControlsSection(): React.ReactElement {
  const { isAdmin } = useWorkspace();
  const orpc = useOrpc();
  const idPrefix = useId();

  // Show the skeleton until the settings load, also when the browser is offline. Nothing failed then, so
  // the "Failed to load" callout would be wrong. (`isLoading` is false for a query paused offline, and
  // `isPending` is true, so the check uses `isPending`.)
  const { data, isPending, error } = useQuery(orpc.settings.get.queryOptions({ input: {} }));

  const mutation = useAiControlsMutation();

  const header = (
    <SettingsSectionHeader
      title="AI Controls"
      description="Decide which workspace knowledge the agent can use or save."
    />
  );

  if (isPending) {
    return (
      <section>
        {header}
        <Skeleton className="h-80 w-full rounded-lg" />
      </section>
    );
  }

  if (!data) {
    return (
      <section>
        {header}
        <Callout variant="error">{error?.message ?? 'Failed to load AI controls.'}</Callout>
      </section>
    );
  }

  const { settings } = data;

  return (
    <section>
      {header}

      <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
        {AI_CONTROLS.map((control) => {
          const id = `${idPrefix}-${control.key}`;
          return (
            <div key={control.key} className="flex items-start justify-between gap-4 px-4 py-4">
              <div>
                {/* A wrapping <label> does not name a button, and the Switch renders one — so the label is
                    tied to the control by id instead. */}
                <label htmlFor={id} className="block text-sm font-medium text-grey-800">
                  {control.title}
                </label>
                <p id={`${id}-description`} className="mt-1.5 text-sm text-grey-500">
                  {control.description}
                </p>
              </div>
              <ActionTooltip show={!isAdmin} message={ADMIN_ONLY}>
                <Switch
                  id={id}
                  aria-describedby={`${id}-description`}
                  checked={settings[control.key]}
                  disabled={!isAdmin || mutation.isPending}
                  // Send only the flag this switch changes. This tab's copy of the other flags can be older
                  // than the server's, and sending it would undo another admin's change.
                  onCheckedChange={(checked) => mutation.mutate({ [control.key]: checked })}
                />
              </ActionTooltip>
            </div>
          );
        })}
      </div>
    </section>
  );
}
